import { Box, Typography, Card } from "@mui/material";
import { styled } from "@mui/material/styles";

const transparentStructureImg = "/assets/transparent-structure.png";
const currencyConversionImg = "/assets/currency-conversion.png";
const amlScreeningImg = "/assets/aml-screening.png";
const reportingApiImg = "/assets/reporting-api.png";

const FeatureCard = styled(Card)(({ theme }) => ({
  background: "var(--overlay-very-light)",
  borderRadius: "40px",
  padding: theme.spacing(5),
  boxShadow: "none",
  height: "100%",
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: "14px",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2.5),
    borderRadius: "20px",
    height: "auto",
  },
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  [theme.breakpoints.down("sm")]: {
    fontSize: "28px",
  },
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
}));

const TransparentStructureCard = styled(FeatureCard)(({ theme }) => ({
  minHeight: "653px",
  justifyContent: "space-between",
  paddingBottom: 0,
  [theme.breakpoints.down("sm")]: {
    minHeight: "500px",
  },
}));

const AmlScreeningCard = styled(FeatureCard)(({ theme }) => ({
  minHeight: "397px",
  paddingBottom: 0,
  [theme.breakpoints.down("sm")]: {
    minHeight: "350px",
  },
}));

const CurrencyConversionCard = styled(FeatureCard)(({ theme }) => ({
  minHeight: "558px",
  paddingBottom: 0,
  [theme.breakpoints.down("sm")]: {
    minHeight: "450px",
  },
}));

const ReportingApiCard = styled(FeatureCard)(({ theme }) => ({
  paddingBottom: 0,
  overflow: "visible",
  [theme.breakpoints.down("sm")]: {
    minHeight: "400px",
  },
}));

const TransparentStructureImage = styled(Box)(() => ({
  width: "100%",
  aspectRatio: "495.748/451.328",
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
}));

const AmlImage = styled(Box)(() => ({
  width: "100%",
  aspectRatio: "510/268",
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
}));

const CurrencyImage = styled(Box)(() => ({
  width: "100%",
  aspectRatio: "510/429",
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
}));

const ReportingImage = styled(Box)(() => ({
  width: "100%",
  aspectRatio: "510/363",
  backgroundSize: "contain",
  backgroundPosition: "center bottom",
  backgroundRepeat: "no-repeat",
  flexShrink: 0,
}));

const BottomImageContainer = styled(Box)(() => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1,
}));

const ReportingImageContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1,
  [theme.breakpoints.down("md")]: {
    alignItems: "flex-end",
    marginRight: `-${theme.spacing(2.5)}`,
    marginBottom: `-${theme.spacing(2.5)}`,
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
  },
}));

const MainGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: "20px",
  alignItems: "center",
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "1fr 1fr",
  },
}));

const LeftColumn = styled(Box)({
  display: "grid",
  gridTemplateRows: "auto auto",
  gap: "20px",
  height: "min-content",
});

const RightColumn = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  gap: "20px",
  [theme.breakpoints.up("md")]: {
    height: "100%",
  },
}));

const FullHeightReportingCard = styled(ReportingApiCard)({
  height: "100%",
  overflow: "hidden", // Но скрываем переполнение для всей карточки
  borderRadius: "40px", // Убеждаемся, что border-radius применяется
});

export default function FeaturesGrid() {
  return (
    <MainGrid>
      <LeftColumn>
        <TransparentStructureCard>
          <FeatureTitle variant="h4">Прозрачная структура</FeatureTitle>
          <FeatureDescription variant="body1">
            UAE компания с PSP лицензией (№2529879.01), KYC-центр.
          </FeatureDescription>
          <TransparentStructureImage
            style={{ backgroundImage: `url('${transparentStructureImg}')` }}
          />
        </TransparentStructureCard>

        <AmlScreeningCard>
          <FeatureTitle variant="h4">AML & Sanctions Screening</FeatureTitle>
          <FeatureDescription variant="body1">
            Автоматические фильтры + ручной контроль.
          </FeatureDescription>
          <BottomImageContainer>
            <AmlImage
              style={{ backgroundImage: `url('${amlScreeningImg}')` }}
            />
          </BottomImageContainer>
        </AmlScreeningCard>
      </LeftColumn>

      <RightColumn>
        <CurrencyConversionCard>
          <FeatureTitle variant="h4">Конвертация валют</FeatureTitle>
          <FeatureDescription variant="body1">
            Киргизская компания меняет валюту внутри кошелька.
          </FeatureDescription>
          <BottomImageContainer>
            <CurrencyImage
              style={{ backgroundImage: `url('${currencyConversionImg}')` }}
            />
          </BottomImageContainer>
        </CurrencyConversionCard>

        <FullHeightReportingCard>
          <FeatureTitle variant="h4">Отчётность и API</FeatureTitle>
          <FeatureDescription variant="body1">
            Готовые выгрузки для compliance и финмониторинга.
          </FeatureDescription>
          <ReportingImageContainer>
            <ReportingImage
              style={{ backgroundImage: `url('${reportingApiImg}')` }}
            />
          </ReportingImageContainer>
        </FullHeightReportingCard>
      </RightColumn>
    </MainGrid>
  );
}
